export const CREDIT_NOTE_SCREEN_ID = "165";
export const DEBIT_NOTE_SCREEN_ID = "166";

export const CREDIT_NOTE_INVOICE_LOOKUP = "customer_invoice_details_credit_note";
export const CREDIT_NOTE_INVOICE_SPLIT_BIND = "invoice_recivebles_split";
export const DEBIT_NOTE_PURCHASE_LOOKUP = "vendor_purchase_details_debit_note";

/** CN/DN submodules keyed on customer/supplier — never infer parent from job_card_id etc. */
export const CREDIT_DEBIT_NOTE_STRICT_PARTY_PARENT_LOOKUPS = new Set([
  CREDIT_NOTE_INVOICE_LOOKUP,
  DEBIT_NOTE_PURCHASE_LOOKUP,
  "credit_note_ledgers_with_parent_id",
  "debit_note_ledgers_with_parent_id",
]);

export const usesStrictCreditDebitNotePartyParent = (
  lookupName?: string | null
): boolean =>
  !!lookupName && CREDIT_DEBIT_NOTE_STRICT_PARTY_PARENT_LOOKUPS.has(lookupName);

/** Resolve parent id from drp_customer/drp_supplier or Json_Attribute ids only. */
export const resolveCreditDebitNotePartyParentId = (
  formData: Record<string, any>,
  parentField: string | undefined
): string | number | null => {
  if (!formData || !parentField) return null;

  const readNonEmpty = (key: string): string | number | null => {
    const v = formData[key];
    if (v === undefined || v === null || String(v).trim() === "") return null;
    return v;
  };

  const fromField = readNonEmpty(parentField);
  if (fromField != null) return fromField;

  if (parentField === "drp_customer") {
    return readNonEmpty("customer_id");
  }
  if (parentField === "drp_supplier") {
    return readNonEmpty("supplier_id");
  }

  return null;
};

export const isCreditNoteInvoiceSplitSubmoduleConfig = (
  subModule?: {
    lookupName?: string | null;
    LookupName?: string | null;
    BindValue?: string | null;
    bindValue?: string | null;
    tableName?: string | null;
    TableName?: string | null;
  } | null
): boolean => {
  if (!subModule) return false;
  const lookup = subModule.LookupName ?? subModule.lookupName;
  const bind = subModule.BindValue ?? subModule.bindValue;
  const table = subModule.TableName ?? subModule.tableName;
  return (
    isCreditNoteInvoiceSubmoduleLookup(lookup) ||
    bind === CREDIT_NOTE_INVOICE_SPLIT_BIND ||
    table === CREDIT_NOTE_INVOICE_SPLIT_BIND
  );
};

export const resolveCreditNoteVoucherId = (
  formData?: Record<string, any> | null
): string | number | null => {
  const id =
    formData?.id ?? formData?.recordId ?? formData?.parentid ?? null;
  if (id === undefined || id === null || String(id).trim() === "") {
    return null;
  }
  return id;
};

/** Read a field from a CN split API/form row (case-insensitive). */
export const readCreditNoteSplitField = (
  row: Record<string, any>,
  ...candidates: string[]
): unknown => {
  const keys = Object.keys(row);
  for (const candidate of candidates) {
    if (
      row[candidate] !== undefined &&
      row[candidate] !== null &&
      String(row[candidate]).trim() !== ""
    ) {
      return row[candidate];
    }
    const match = keys.find((k) => k.toLowerCase() === candidate.toLowerCase());
    if (
      match !== undefined &&
      row[match] !== undefined &&
      row[match] !== null &&
      String(row[match]).trim() !== ""
    ) {
      return row[match];
    }
  }
  return undefined;
};

/** True when GET /submodule returned a persisted CN split line (id + invoice). */
export const isCreditNoteSplitApiRowPersisted = (
  row: unknown
): boolean => {
  if (!row || typeof row !== "object") return false;
  const r = row as Record<string, any>;
  const invoiceNo = String(
    readCreditNoteSplitField(
      r,
      "invoice_no",
      "txt_invoice_no",
      "Invoice_No"
    ) ?? ""
  ).trim();
  const id = String(readCreditNoteSplitField(r, "id") ?? "").trim();
  return invoiceNo !== "" && id !== "";
};

/** True when GET /submodule returned a usable CN split line. */
export const isCreditNoteSplitApiRowMeaningful = (
  row: unknown
): boolean => {
  if (!row || typeof row !== "object") return false;
  const r = row as Record<string, any>;
  const invoiceNo = String(
    readCreditNoteSplitField(
      r,
      "invoice_no",
      "txt_invoice_no",
      "Invoice_No"
    ) ?? ""
  ).trim();
  const amount =
    parseFloat(
      String(
        readCreditNoteSplitField(
          r,
          "amount",
          "txt_amount",
          "Amount",
          "allocated_amount",
          "voucher_paid",
          "cn_amount"
        ) ?? 0
      )
    ) || 0;
  const id = String(readCreditNoteSplitField(r, "id") ?? "").trim();
  return (
    (invoiceNo !== "" && amount > 0.001) ||
    (id !== "" && amount > 0.001) ||
    isCreditNoteSplitApiRowPersisted(row)
  );
};

export const creditNoteSplitApiRowsAreMeaningful = (rows: unknown): boolean =>
  Array.isArray(rows) &&
  rows.length > 0 &&
  rows.some(
    (row) =>
      isCreditNoteSplitApiRowMeaningful(row) ||
      isCreditNoteSplitApiRowPersisted(row)
  );

/** CN voucher footer / header net amount used to restore split Amount on edit. */
export const resolveCreditNoteAllocatedNetAmount = (
  formData?: Record<string, any> | null
): number | null => {
  if (!formData) return null;
  const raw = formData.net_amount ?? formData.txt_net_amount;
  if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
    const parsed = parseFloat(String(raw));
    if (Number.isFinite(parsed) && parsed > 0.001) return parsed;
  }
  const voucherRows = formData.voucher_details;
  if (Array.isArray(voucherRows) && voucherRows.length > 0) {
    const total = voucherRows.reduce((sum, row) => {
      if (!row || typeof row !== "object") return sum;
      const credit = parseFloat(
        String((row as Record<string, any>).credit ?? (row as Record<string, any>).txt_credit ?? 0)
      );
      return sum + (Number.isFinite(credit) ? credit : 0);
    }, 0);
    if (total > 0.001) return total;
  }
  return null;
};

/** Fill zero split amounts from voucher net when submodule row exists but amount column is blank. */
export const applyCreditNoteNetAmountToSplitRows = (
  rows: Record<string, any>[],
  formData?: Record<string, any> | null,
  lookupName?: string | null
): Record<string, any>[] => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const net = resolveCreditNoteAllocatedNetAmount(formData);
  if (net === null) return rows;

  const zeroAmountRows = rows.filter((row) => {
    const amount =
      parseFloat(String(row.amount ?? row.txt_amount ?? 0)) || 0;
    return amount <= 0.001 && isCreditDebitNoteSplitRowShape(row);
  });
  if (zeroAmountRows.length === 0) return rows;

  if (rows.length === 1 && zeroAmountRows.length === 1) {
    return rows.map((row) =>
      normalizeCreditDebitNoteSplitRow(
        {
          ...row,
          amount: String(net),
          txt_amount: String(net),
        },
        lookupName
      )
    );
  }

  if (zeroAmountRows.length === rows.length && rows.length > 1) {
    let remaining = net;
    return rows.map((row, index) => {
      const share =
        index === rows.length - 1
          ? remaining
          : Math.round((net / rows.length) * 1000) / 1000;
      remaining -= share;
      return normalizeCreditDebitNoteSplitRow(
        {
          ...row,
          amount: String(share),
          txt_amount: String(share),
        },
        lookupName
      );
    });
  }

  return rows;
};

/** CN edit/view: overlay split Amount onto template rows when saved fetch failed. */
export const restoreCreditNoteInvoiceSplitAmounts = (
  templateRows: Record<string, any>[],
  formData?: Record<string, any> | null,
  bindRows?: unknown,
  lookupName?: string | null,
  savedRefRows: Record<string, any>[] = []
): Record<string, any>[] => {
  return resolveCreditNoteInvoiceSubModuleRows(
    formData,
    templateRows,
    bindRows,
    lookupName,
    savedRefRows
  );
};

/** Normalize raw GET /submodule row onto txt_* + Json_Attribute keys. */
export const coerceCreditNoteSplitApiRow = (
  item: Record<string, any>,
  lookupName?: string | null,
  controls?: any[]
): Record<string, any> => {
  const row: Record<string, any> =
    item && typeof item === "object" ? { ...item } : {};

  if (Array.isArray(controls)) {
    for (const control of controls) {
      const fieldName =
        control.name || control.BindValue || control.bindValue;
      const jsonAttr =
        control.Json_Attribute ??
        control.json_Attribute ??
        control.json_attribute;
      if (!fieldName || !jsonAttr) continue;
      const value = readCreditNoteSplitField(
        row,
        String(jsonAttr),
        String(fieldName)
      );
      if (value === undefined) continue;
      row[fieldName] = value;
      row[jsonAttr] = value;
    }
  }

  const invoiceNo = readCreditNoteSplitField(
    row,
    "invoice_no",
    "txt_invoice_no",
    "Invoice_No"
  );
  if (invoiceNo !== undefined) {
    const invoiceStr = String(invoiceNo);
    row.invoice_no = invoiceStr;
    row.txt_invoice_no = invoiceStr;
  }

  const amount = pickNonZeroAmount(
    readCreditNoteSplitField(
      row,
      "amount",
      "txt_amount",
      "Amount",
      "allocated_amount",
      "voucher_paid",
      "cn_amount"
    ),
    row.amount,
    row.txt_amount
  );
  if (amount !== undefined) {
    const amountStr = String(amount);
    row.amount = amountStr;
    row.txt_amount = amountStr;
  }

  const parentId = readCreditNoteSplitField(
    row,
    "parent_id",
    "parentid",
    "Parent_Id"
  );
  if (parentId !== undefined) {
    const parentStr = String(parentId);
    row.parent_id = parentStr;
    row.parentid = parentStr;
  }

  const lineId = readCreditNoteSplitField(row, "id");
  if (lineId !== undefined) {
    row.id = String(lineId);
    row.act = row.act ?? "edit";
  }

  return normalizeCreditDebitNoteSplitRow(row, lookupName);
};

export const mapCreditNoteSplitApiRows = (
  apiRows: any[],
  lookupName?: string | null,
  controls?: any[]
): Record<string, any>[] => {
  if (!Array.isArray(apiRows) || apiRows.length === 0) return [];
  return apiRows.map((item) =>
    coerceCreditNoteSplitApiRow(
      item && typeof item === "object" ? (item as Record<string, any>) : {},
      lookupName,
      controls
    )
  );
};

/** CN-only: extract persisted split lines (invoice + amount). */
export const pickCreditNoteSplitRowsWithAmount = (
  rows: unknown,
  formData?: Record<string, any> | null,
  lookupName?: string | null
): Record<string, any>[] => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const withAmount = rows
    .map((r) =>
      r && typeof r === "object"
        ? coerceCreditNoteSplitApiRow(r as Record<string, any>, lookupName)
        : null
    )
    .filter((row): row is Record<string, any> => {
      if (!row) return false;
      const invoiceNo = String(row.invoice_no ?? row.txt_invoice_no ?? "").trim();
      const amount =
        parseFloat(String(row.amount ?? row.txt_amount ?? 0)) || 0;
      return invoiceNo !== "" && amount > 0.001;
    });

  if (withAmount.length > 0) return withAmount;

  const persisted = rows
    .map((r) =>
      r && typeof r === "object"
        ? coerceCreditNoteSplitApiRow(r as Record<string, any>, lookupName)
        : null
    )
    .filter((row): row is Record<string, any> => {
      if (!row) return false;
      return isCreditNoteSplitApiRowPersisted(row);
    });

  return applyCreditNoteNetAmountToSplitRows(persisted, formData, lookupName);
};

export const hasCreditNoteSplitRowsWithAmount = (
  rows: unknown,
  voucherId?: string | number | null,
  formData?: Record<string, any> | null,
  lookupName?: string | null
): boolean => {
  return (
    pickCreditNoteSplitRowsWithAmount(rows, formData, lookupName).length > 0
  );
};

export const isCreditNoteScreen = (screenId?: string | null): boolean =>
  String(screenId || "") === CREDIT_NOTE_SCREEN_ID;

export const isDebitNoteScreen = (screenId?: string | null): boolean =>
  String(screenId || "") === DEBIT_NOTE_SCREEN_ID;

export const isCreditOrDebitNoteScreen = (screenId?: string | null): boolean =>
  isCreditNoteScreen(screenId) || isDebitNoteScreen(screenId);

export const isCreditNoteInvoiceSubmoduleLookup = (
  lookupName?: string | null
): boolean => lookupName === CREDIT_NOTE_INVOICE_LOOKUP;

export const isDebitNotePurchaseSubmoduleLookup = (
  lookupName?: string | null
): boolean => lookupName === DEBIT_NOTE_PURCHASE_LOOKUP;

export const isCreditDebitNoteSplitSubmoduleLookup = (
  lookupName?: string | null
): boolean =>
  isCreditNoteInvoiceSubmoduleLookup(lookupName) ||
  isDebitNotePurchaseSubmoduleLookup(lookupName);

const pickField = (...candidates: unknown[]): unknown => {
  for (const value of candidates) {
    if (value === 0 || value === "0") return value;
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return undefined;
};

/** Prefer first positive numeric amount; fall back to explicit zero. */
export const pickNonZeroAmount = (...candidates: unknown[]): unknown => {
  let zeroFallback: unknown = undefined;
  for (const value of candidates) {
    if (value === undefined || value === null || String(value).trim() === "") {
      continue;
    }
    const num = parseFloat(String(value));
    if (Number.isFinite(num) && num > 0.001) {
      return value;
    }
    if (value === 0 || value === "0") {
      zeroFallback = value;
    }
  }
  return zeroFallback;
};

export const isCreditDebitNoteSplitRowShape = (
  row: Record<string, any>
): boolean => {
  if (!row || typeof row !== "object") return false;
  const invoiceNo = String(row.invoice_no ?? row.txt_invoice_no ?? "").trim();
  const purchaseNo = String(row.purchase_no ?? row.txt_purchase_no ?? "").trim();
  return invoiceNo !== "" || purchaseNo !== "";
};

/** Lookup template rows carry service_invoice/purchase id but no voucher parent_id. */
export const isCreditDebitNoteLookupTemplateRow = (
  row: Record<string, any>,
  voucherId?: string | number | null
): boolean => {
  if (!isCreditDebitNoteSplitRowShape(row)) return false;

  const amount = parseFloat(String(row.amount ?? row.txt_amount ?? 0)) || 0;
  if (amount > 0.001) return false;

  const parentId = row.parent_id ?? row.parentid;
  if (
    parentId === undefined ||
    parentId === null ||
    String(parentId).trim() === ""
  ) {
    return true;
  }
  if (
    voucherId !== undefined &&
    voucherId !== null &&
    String(voucherId).trim() !== ""
  ) {
    return String(parentId).trim() !== String(voucherId).trim();
  }
  return false;
};

/** CN-only: every row is a lookup template (not voucher-persisted split). */
export const areCreditNoteLookupTemplateRows = (
  rows: unknown,
  voucherId?: string | number | null
): boolean => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.every(
    (r) =>
      r &&
      typeof r === "object" &&
      isCreditDebitNoteSplitRowShape(r as Record<string, any>) &&
      isCreditDebitNoteLookupTemplateRow(r as Record<string, any>, voucherId)
  );
};

/** CN edit/view: value already has merged split amounts (not lookup templates). */
export const isCreditNoteEditWithMergedSplitValue = (
  value: unknown,
  formData?: Record<string, any> | null
): boolean => {
  const voucherId = resolveCreditNoteVoucherId(formData);
  const rows = Array.isArray(value) ? value : [];
  return (
    hasCreditNoteSplitRowsWithAmount(rows, voucherId) &&
    !areCreditNoteLookupTemplateRows(rows, voucherId)
  );
};

/** Rows persisted under invoice_recivebles_split / purchase_payment_split (edit load). */
export const hasCreditDebitNoteSplitRows = (
  rows: unknown,
  voucherId?: string | number | null
): boolean => {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some((r) => {
    if (!r || typeof r !== "object") return false;
    const row = r as Record<string, any>;
    const lineId = row.id;
    if (lineId === undefined || lineId === null || String(lineId).trim() === "") {
      return false;
    }
    const invoiceNo = String(row.invoice_no ?? row.txt_invoice_no ?? "").trim();
    const purchaseNo = String(row.purchase_no ?? row.txt_purchase_no ?? "").trim();
    if (invoiceNo === "" && purchaseNo === "") return false;

    const parentId = row.parent_id ?? row.parentid;
    const hasParent =
      parentId !== undefined &&
      parentId !== null &&
      String(parentId).trim() !== "";
    if (hasParent) {
      if (
        voucherId !== undefined &&
        voucherId !== null &&
        String(voucherId).trim() !== ""
      ) {
        return String(parentId).trim() === String(voucherId).trim();
      }
      return false;
    }

    const amount = parseFloat(String(row.amount ?? row.txt_amount ?? 0)) || 0;
    return amount > 0.001;
  });
};

export const hasPersistedCreditDebitNoteSplitRows = hasCreditDebitNoteSplitRows;

export const normalizeCreditDebitNoteSplitRow = (
  row: Record<string, any>,
  lookupName?: string | null
): Record<string, any> => {
  if (!row || typeof row !== "object") return row;
  const r: Record<string, any> = { ...row };
  const syncPair = (bindKey: string, jsonKey: string) => {
    const picked = pickField(r[bindKey], r[jsonKey]);
    if (picked === undefined) return;
    const value = String(picked);
    r[bindKey] = value;
    r[jsonKey] = value;
  };

  if (isCreditNoteInvoiceSubmoduleLookup(lookupName)) {
    syncPair("txt_invoice_no", "invoice_no");
  }
  if (isDebitNotePurchaseSubmoduleLookup(lookupName)) {
    syncPair("txt_purchase_no", "purchase_no");
  }

  syncPair("txt_total_amount", "total_amount");
  syncPair("txt_paid_amount", "amount_paid");
  syncPair("txt_discount_amount", "discounted_amount");
  syncPair("txt_due_amount", "due_amount");
  const amountPicked = pickNonZeroAmount(r.txt_amount, r.amount);
  if (amountPicked !== undefined) {
    const amountStr = String(amountPicked);
    r.txt_amount = amountStr;
    r.amount = amountStr;
  }
  syncPair("txt_discount", "discount");

  if (r.id && !r.act) {
    r.act = "edit";
  }
  return r;
};

const splitDocumentKey = (
  row: Record<string, any>,
  lookupName?: string | null
): string => {
  if (isDebitNotePurchaseSubmoduleLookup(lookupName)) {
    return String(row.purchase_no ?? row.txt_purchase_no ?? "").trim();
  }
  return String(row.invoice_no ?? row.txt_invoice_no ?? "").trim();
};

/** Overlay saved credit/debit note split amounts onto lookup invoice/purchase template rows. */
export const mergeCreditDebitNoteSplitSavedRows = (
  templateRows: Record<string, any>[],
  savedRows: Record<string, any>[],
  lookupName?: string | null
): Record<string, any>[] => {
  const normalizedSaved = (savedRows || []).map((row) =>
    normalizeCreditDebitNoteSplitRow(row, lookupName)
  );
  if (!normalizedSaved.length) {
    return (templateRows || []).map((row) =>
      normalizeCreditDebitNoteSplitRow(row, lookupName)
    );
  }

  const savedByKey = new Map<string, Record<string, any>>();
  for (const saved of normalizedSaved) {
    const key = splitDocumentKey(saved, lookupName);
    if (key) savedByKey.set(key, saved);
  }

  if (!templateRows?.length) {
    return normalizedSaved;
  }

  const merged = templateRows.map((template) => {
    const base = normalizeCreditDebitNoteSplitRow(template, lookupName);
    const key = splitDocumentKey(base, lookupName);
    const saved = key ? savedByKey.get(key) : undefined;
    if (!saved) return base;

    const amount = pickNonZeroAmount(
      saved.txt_amount,
      saved.amount,
      base.txt_amount,
      base.amount
    );
    return normalizeCreditDebitNoteSplitRow(
      {
        ...base,
        ...saved,
        txt_invoice_no: pickField(saved.txt_invoice_no, saved.invoice_no, base.txt_invoice_no, base.invoice_no),
        invoice_no: pickField(saved.invoice_no, saved.txt_invoice_no, base.invoice_no, base.txt_invoice_no),
        txt_purchase_no: pickField(saved.txt_purchase_no, saved.purchase_no, base.txt_purchase_no, base.purchase_no),
        purchase_no: pickField(saved.purchase_no, saved.txt_purchase_no, base.purchase_no, base.txt_purchase_no),
        txt_amount: amount,
        amount,
        id: pickField(saved.id, base.id),
        parent_id: pickField(saved.parent_id, saved.parentid, base.parent_id, base.parentid),
        act: pickField(saved.act, "edit"),
      },
      lookupName
    );
  });

  for (const saved of normalizedSaved) {
    const key = splitDocumentKey(saved, lookupName);
    if (!key) continue;
    if (merged.some((row) => splitDocumentKey(row, lookupName) === key)) continue;
    merged.push(saved);
  }

  return merged;
};

/** CN-only: overlay saved split amount onto templates; preserve lookup totals. */
export const mergeCreditNoteSplitSavedRows = (
  templateRows: Record<string, any>[],
  savedRows: Record<string, any>[],
  lookupName?: string | null
): Record<string, any>[] => {
  const normalizedSaved = (savedRows || []).map((row) =>
    coerceCreditNoteSplitApiRow(row, lookupName)
  );
  if (!normalizedSaved.length) {
    return (templateRows || []).map((row) =>
      normalizeCreditDebitNoteSplitRow(row, lookupName)
    );
  }

  const savedByKey = new Map<string, Record<string, any>>();
  for (const saved of normalizedSaved) {
    const key = splitDocumentKey(saved, lookupName);
    if (key) savedByKey.set(key, saved);
  }

  if (!templateRows?.length) {
    return normalizedSaved;
  }

  const merged = templateRows.map((template) => {
    const base = normalizeCreditDebitNoteSplitRow(template, lookupName);
    const key = splitDocumentKey(base, lookupName);
    const saved = key ? savedByKey.get(key) : undefined;
    if (!saved) return base;

    const amount = pickNonZeroAmount(
      saved.txt_amount,
      saved.amount,
      base.txt_amount,
      base.amount
    );
    return normalizeCreditDebitNoteSplitRow(
      {
        ...base,
        txt_invoice_no: pickField(
          saved.txt_invoice_no,
          saved.invoice_no,
          base.txt_invoice_no,
          base.invoice_no
        ),
        invoice_no: pickField(
          saved.invoice_no,
          saved.txt_invoice_no,
          base.invoice_no,
          base.txt_invoice_no
        ),
        txt_amount: amount,
        amount,
        id: pickField(saved.id, base.id),
        parent_id: pickField(
          saved.parent_id,
          saved.parentid,
          base.parent_id,
          base.parentid
        ),
        parentid: pickField(
          saved.parentid,
          saved.parent_id,
          base.parentid,
          base.parent_id
        ),
        act: pickField(saved.act, "edit"),
      },
      lookupName
    );
  });

  for (const saved of normalizedSaved) {
    const key = splitDocumentKey(saved, lookupName);
    if (!key) continue;
    if (merged.some((row) => splitDocumentKey(row, lookupName) === key)) {
      continue;
    }
    merged.push(saved);
  }

  return merged;
};

export const resolveCreditNoteSavedSplitRows = (
  formData: Record<string, any> | null | undefined,
  savedRefRows: Record<string, any>[] = [],
  voucherId?: string | number | null,
  lookupName?: string | null
): Record<string, any>[] => {
  const pickedRef = pickCreditNoteSplitRowsWithAmount(
    savedRefRows,
    formData,
    lookupName
  );
  if (pickedRef.length > 0) return pickedRef;

  if (!formData) return [];
  for (const key of ["invoice_recivebles_split", "invoice_recivebles"]) {
    const picked = pickCreditNoteSplitRowsWithAmount(
      formData[key],
      formData,
      lookupName
    );
    if (picked.length > 0) return picked;
    const rows = formData[key];
    if (hasCreditDebitNoteSplitRows(rows, voucherId)) {
      const persisted = Array.isArray(rows)
        ? applyCreditNoteNetAmountToSplitRows(
            (rows as Record<string, any>[]).map((row) =>
              coerceCreditNoteSplitApiRow(row, lookupName)
            ),
            formData,
            lookupName
          )
        : [];
      if (persisted.length > 0) return persisted;
    }
  }
  return [];
};

/** CN-only: skip replacing saved split rows with lookup template rows. */
export const shouldSkipCreditNoteTemplateSubmoduleOverwrite = (
  existing: unknown,
  incoming: unknown,
  voucherId?: string | number | null
): boolean => {
  if (!Array.isArray(incoming) || incoming.length === 0) return false;
  if (!hasCreditNoteSplitRowsWithAmount(existing, voucherId)) return false;
  return areCreditNoteLookupTemplateRows(incoming, voucherId);
};

/** Dev-only trace for CN invoice split view/load issues. */
export const logCreditNoteSplitDebug = (
  stage: string,
  payload: Record<string, unknown>
): void => {
  if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
    console.info(`[CN split:${stage}]`, payload);
  }
};

/** Resolve rows to pass into CN invoice SubModule (templates + saved amount overlay). */
export const resolveCreditNoteInvoiceSubModuleRows = (
  formData: Record<string, any> | null | undefined,
  nameRows: unknown,
  bindRows: unknown,
  lookupName?: string | null,
  savedRefRows: Record<string, any>[] = []
): Record<string, any>[] => {
  const voucherId = resolveCreditNoteVoucherId(formData);
  const saved = resolveCreditNoteSavedSplitRows(
    formData,
    savedRefRows,
    voucherId,
    lookupName
  ).map((row) => normalizeCreditDebitNoteSplitRow(row, lookupName));

  if (!saved.length) {
    const net = resolveCreditNoteAllocatedNetAmount(formData);
    const templateSource = [
      ...(Array.isArray(nameRows) ? (nameRows as Record<string, any>[]) : []),
      ...(Array.isArray(bindRows) ? (bindRows as Record<string, any>[]) : []),
    ].filter((row) => isCreditDebitNoteSplitRowShape(row));
    if (net !== null && templateSource.length === 1) {
      const fallbackSaved = applyCreditNoteNetAmountToSplitRows(
        templateSource.map((row) => ({ ...row })),
        formData,
        lookupName
      );
      if (pickCreditNoteSplitRowsWithAmount(fallbackSaved, formData, lookupName).length > 0) {
        logCreditNoteSplitDebug("net-amount-fallback", {
          net,
          invoice_no:
            fallbackSaved[0]?.invoice_no ?? fallbackSaved[0]?.txt_invoice_no,
        });
        const merged = mergeCreditNoteSplitSavedRows(
          templateSource,
          fallbackSaved,
          lookupName
        );
        return merged.length > 0 ? merged : fallbackSaved;
      }
    }
  }

  const templates = Array.isArray(nameRows) ? (nameRows as Record<string, any>[]) : [];
  const bindTemplateRows = Array.isArray(bindRows)
    ? (bindRows as Record<string, any>[])
    : [];
  const meaningfulTemplates = templates.filter((row) =>
    isCreditDebitNoteSplitRowShape(row)
  );
  const lookupTemplateRows = [
    ...meaningfulTemplates,
    ...bindTemplateRows.filter((row) =>
      isCreditDebitNoteLookupTemplateRow(row, voucherId)
    ),
  ];

  logCreditNoteSplitDebug("resolve", {
    voucherId,
    savedCount: saved.length,
    templateCount: templates.length,
    bindCount: bindTemplateRows.length,
    lookupTemplateCount: lookupTemplateRows.length,
    savedSample: saved[0]
      ? {
          invoice_no: saved[0].invoice_no ?? saved[0].txt_invoice_no,
          amount: saved[0].amount ?? saved[0].txt_amount,
        }
      : null,
  });

  if (!saved.length) {
    if (meaningfulTemplates.length > 0) return meaningfulTemplates;
    return bindTemplateRows.filter((row) => isCreditDebitNoteSplitRowShape(row));
  }

  const templateSource = lookupTemplateRows;

  if (
    templateSource.length > 0 &&
    areCreditNoteLookupTemplateRows(templateSource, voucherId)
  ) {
    const merged = mergeCreditNoteSplitSavedRows(
      templateSource,
      saved,
      lookupName
    );
    logCreditNoteSplitDebug("merged-templates", { count: merged.length, merged });
    return merged.length > 0 ? merged : saved;
  }

  if (
    templateSource.length > 0 &&
    hasCreditDebitNoteSplitRows(templateSource, voucherId) &&
    !areCreditNoteLookupTemplateRows(templateSource, voucherId)
  ) {
    return templateSource;
  }

  logCreditNoteSplitDebug("saved-only", { count: saved.length });
  return saved;
};

/** Resolve persisted CN/DN split rows from ref, formData, or value prop (edit/view amount overlay). */
export const resolveCreditDebitNoteSavedSplitRows = (
  formData: Record<string, any> | null | undefined,
  savedRefRows: Record<string, any>[] = [],
  valueRows?: unknown,
  lookupName?: string | null,
  voucherId?: string | number | null
): Record<string, any>[] => {
  if (
    savedRefRows.length > 0 &&
    hasCreditDebitNoteSplitRows(savedRefRows, voucherId)
  ) {
    return savedRefRows;
  }

  if (formData) {
    const bindKeys = isDebitNotePurchaseSubmoduleLookup(lookupName)
      ? ["purchase_payment_split", "purchase_recivebles", "purchase_details"]
      : ["invoice_recivebles_split", "invoice_recivebles"];
    for (const key of bindKeys) {
      const rows = formData[key];
      if (hasCreditDebitNoteSplitRows(rows, voucherId)) {
        return Array.isArray(rows) ? (rows as Record<string, any>[]) : [];
      }
    }
  }

  if (hasCreditDebitNoteSplitRows(valueRows, voucherId)) {
    return Array.isArray(valueRows) ? (valueRows as Record<string, any>[]) : [];
  }

  return [];
};

const sumSplitRowAmounts = (rows: unknown): number | null => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const total = rows.reduce((sum, row) => {
    if (!row || typeof row !== "object") return sum;
    const amount = parseFloat(String((row as Record<string, any>).txt_amount ?? (row as Record<string, any>).amount ?? 0));
    return sum + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  return total;
};

/** Resolve allocated net amount from footer or split row totals. */
export const resolveCreditDebitNoteNetAmount = (
  formData: Record<string, any> | null | undefined
): number | null => {
  if (!formData) return null;

  const raw = formData.net_amount ?? formData.txt_net_amount;
  if (raw !== undefined && raw !== null && raw !== "") {
    const parsed = parseFloat(String(raw));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const fromInvoices = sumSplitRowAmounts(formData.invoice_recivebles_split);
  if (fromInvoices !== null) return fromInvoices;

  const fromPurchases = sumSplitRowAmounts(formData.purchase_payment_split);
  if (fromPurchases !== null) return fromPurchases;

  return null;
};

/** Keep BindValue + Json_Attribute credit fields aligned for voucher detail rows. */
export const syncVoucherDetailCreditFields = (
  row: Record<string, any>
): Record<string, any> => {
  if (!row || typeof row !== "object") return row;
  const r: Record<string, any> = { ...row };
  const credit = r.txt_credit ?? r.credit;
  if (credit !== undefined && credit !== null && String(credit).trim() !== "") {
    const value = String(credit);
    r.txt_credit = value;
    r.credit = value;
  }
  return r;
};

export const applyNetAmountToPartyLedgerVoucherRows = (
  rows: Record<string, any>[] | null | undefined,
  netAmount: number
): Record<string, any>[] | null => {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const first = rows[0];
  if (!first || typeof first !== "object") return null;

  const current = parseFloat(String(first.txt_credit ?? first.credit ?? 0)) || 0;
  if (Math.abs(current - netAmount) < 0.001) return null;
  if (netAmount <= 0.001 && current > 0.001) return null;

  const amountStr = netAmount === 0 ? "0" : String(netAmount);
  const updatedFirst = syncVoucherDetailCreditFields({
    ...first,
    txt_credit: amountStr,
    credit: amountStr,
  });

  return [updatedFirst, ...rows.slice(1)];
};

export const buildCreditNoteVoucherLedgerRow = (
  customerId: string | number,
  formData: Record<string, any>
): Record<string, any> => {
  const selected =
    (formData?.drp_customer_selectedData as Record<string, any>) || {};
  const label =
    selected.customername ??
    selected.name ??
    selected.label ??
    formData?.drp_customer_desc ??
    "";
  return syncVoucherDetailCreditFields({
    drp_ledger: String(customerId),
    ledger_id: String(customerId),
    drp_ledger_desc: String(label),
    ledger_id_desc: String(label),
    txt_credit: "0",
    credit: "0",
    txt_remarks: "",
    remarks: "",
  });
};

export const buildDebitNoteVoucherLedgerRow = (
  supplierId: string | number,
  formData: Record<string, any>
): Record<string, any> => {
  const selected =
    (formData?.drp_supplier_selectedData as Record<string, any>) || {};
  const label =
    selected.suppliername ??
    selected.name ??
    selected.label ??
    formData?.drp_supplier_desc ??
    "";
  return syncVoucherDetailCreditFields({
    drp_ledger: String(supplierId),
    ledger_id: String(supplierId),
    drp_ledger_desc: String(label),
    ledger_id_desc: String(label),
    txt_credit: "0",
    credit: "0",
    txt_remarks: "",
    remarks: "",
  });
};
